/**
 * Where team badges come from.
 *
 * They used to come from REACT_APP_S3_TEAM_BADGES — one bucket baked into the
 * build for every organisation and every scene. That is wrong in two ways: a
 * badge is a SCENE ASSET, so it belongs with the scene the graphics actually
 * render from, and a build-time constant cannot follow an operator who switches
 * to a scene with its own artwork.
 *
 * The scene already carries `load_team_badge_path` (alongside load_player_path
 * and load_part_path) and the scene API already returns it — Drive was simply
 * not reading it. It is a RELATIVE path, so it joins onto the same
 * `${RENDERER_PREVIEW_URL}/${scene_path}` base that caption definitions are
 * already loaded from (see the preset construction in TXLive), which keeps
 * every scene asset resolving through one host.
 *
 * The env var stays as the fallback and is NOT deprecated: only a handful of
 * scenes populate load_team_badge_path today, so most still need it.
 *
 * Resolution order for a badge, most specific first:
 *   1. the team's own explicit team_logo_url  (an operator override)
 *   2. the scene's badge folder               (this change)
 *   3. REACT_APP_S3_TEAM_BADGES               (the previous behaviour)
 *   4. the caller's fallback image
 */

/** Trailing/leading slashes make path joining a coin flip; normalise once. */
const trimEnd = (s) => String(s || '').replace(/\/+$/, '');
const trimStart = (s) => String(s || '').replace(/^\/+/, '');
/** Strip BOTH ends — a segment in the middle of a join must own neither slash. */
const trimBoth = (s) => trimEnd(trimStart(s));

/**
 * Build the folder that team badge images live in for the active scene.
 *
 * @param {object} renderer the selected renderer, carrying scene_path and the
 *   scene object merged by apiService
 * @returns {string|null} a base URL with no trailing slash, or null when this
 *   scene does not define one
 */
export function resolveSceneBadgeBase(renderer) {
  if (!renderer) return null;

  // Same field, three shapes depending on which endpoint supplied the renderer.
  const badgePath =
    renderer.scene?.load_team_badge_path
    ?? renderer.load_team_badge_path
    ?? renderer.allocation_details?.scene?.load_team_badge_path
    ?? null;
  if (!badgePath) return null;

  const scenePath = renderer.scene_path ?? renderer.scene?.path ?? renderer.scene?.scene_path;
  if (!scenePath) return null;

  // Most scene_paths are relative ("/scenes/ae-group/bluebird-mvp") and hang off
  // the renderer host; one or two are already absolute, in which case that host
  // wins and the env base must not be prepended.
  const isAbsolute = /^https?:\/\//i.test(scenePath);
  const host = isAbsolute ? '' : trimEnd(process.env.REACT_APP_RENDERER_PREVIEW_URL);

  const joined = isAbsolute
    ? `${trimEnd(scenePath)}/${trimBoth(badgePath)}`
    : `${host}/${trimBoth(scenePath)}/${trimBoth(badgePath)}`;

  return trimEnd(joined);
}

/** The bucket Drive shipped with. Still the fallback for unconfigured scenes. */
export function resolveEnvBadgeBase() {
  const base = trimEnd(process.env.REACT_APP_S3_TEAM_BADGES);
  return base || null;
}

/**
 * The badge image for one team.
 *
 * `_H` is the home-kit crest variant, matching the filenames already in the S3
 * bucket; the scene folders follow the same convention.
 *
 * @param {object} team must carry team_tricode, may carry team_logo_url
 * @param {{renderer?: object, fallback?: any}} [options]
 */
export function resolveTeamLogo(team, { renderer = null, fallback = null } = {}) {
  if (!team) return fallback;
  // An explicit URL on the team always wins — it is a deliberate override.
  if (team.team_logo_url) return team.team_logo_url;

  const base = resolveSceneBadgeBase(renderer) || resolveEnvBadgeBase();
  if (!base || !team.team_tricode) return fallback;

  return `${base}/${team.team_tricode}_H.webp`;
}

export default resolveTeamLogo;
